// AdminDashboard.js - Complete admin panel for managing users and blog posts
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi, blogApi } from '../../Services/Api';
import styles from './AdminDashboard.module.css';

const AdminDashboard = ({ userData, onLogout }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  
  // Filters
  const [userFilter, setUserFilter] = useState('all');
  const [userSearch, setUserSearch] = useState('');
  const [postStatusFilter, setPostStatusFilter] = useState('pending');
  
  // Pagination
  const [userPage, setUserPage] = useState(1);
  const [userPagination, setUserPagination] = useState({});
  
  // Modals
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const isAdmin = userData?.is_staff || userData?.is_superuser;
  const isSuperuser = userData?.is_superuser;

  useEffect(() => {
    if (isAdmin) {
      fetchStats();
    }
  }, [isAdmin]);

  useEffect(() => {
    if (activeTab === 'users' && isAdmin) {
      fetchUsers();
    }
  }, [activeTab, userFilter, userSearch, userPage, isAdmin]);

  useEffect(() => {
    if (activeTab === 'blog' && isAdmin) {
      fetchPosts();
    }
  }, [activeTab, postStatusFilter, isAdmin]);

  const fetchStats = async () => {
    try {
      const response = await adminApi.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await adminApi.getUsers({
        filter: userFilter,
        search: userSearch,
        page: userPage,
        per_page: 15
      });
      setUsers(response.data.users);
      setUserPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchPosts = async () => {
    try {
      const response = await blogApi.getModerationQueue(postStatusFilter);
      setPosts(response.data.posts);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  };

  const handleUserAction = async (userId, action) => {
    if (action === 'delete' && !window.confirm('Are you sure you want to delete this user? This cannot be undone.')) {
      return;
    }
    
    setActionLoading(userId);
    try {
      await adminApi.userAction(userId, action);
      fetchUsers();
      fetchStats();
      setSelectedUser(null);
    } catch (error) {
      alert(error.response?.data?.error || 'Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleApprovePost = async (postId) => {
    setActionLoading(postId);
    try {
      await blogApi.moderatePost(postId, { action: 'approve' });
      setPosts(posts.filter(p => p.id !== postId));
      fetchStats();
      setSelectedPost(null);
    } catch (error) {
      console.error('Error approving post:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectPost = async (postId) => {
    if (!rejectionReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    setActionLoading(postId);
    try {
      await blogApi.moderatePost(postId, { 
        action: 'reject', 
        rejection_reason: rejectionReason 
      });
      setPosts(posts.filter(p => p.id !== postId));
      fetchStats();
      setSelectedPost(null);
      setRejectionReason('');
    } catch (error) {
      console.error('Error rejecting post:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Access denied
  if (!isAdmin) {
    return (
      <div className={styles.container}>
        <div className={styles.accessDenied}>
          <span className={styles.lockIcon}>🔒</span>
          <h2>Admin Access Required</h2>
          <p>You need administrator privileges to access this page.</p>
          <button onClick={() => navigate('/dashboard')}>Go to Dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Sidebar */}
      <aside className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <span className={styles.adminBadge}>🛡️ Admin</span>
          <h2>{userData?.username}</h2>
          <p>{isSuperuser ? 'Super Admin' : 'Staff'}</p>
        </div>
        
        <nav className={styles.nav}>
          <button 
            className={`${styles.navItem} ${activeTab === 'overview' ? styles.active : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <span>📊</span> Overview
          </button>
          <button 
            className={`${styles.navItem} ${activeTab === 'users' ? styles.active : ''}`}
            onClick={() => setActiveTab('users')}
          >
            <span>👥</span> Users
          </button>
          <button 
            className={`${styles.navItem} ${activeTab === 'blog' ? styles.active : ''}`}
            onClick={() => setActiveTab('blog')}
          >
            <span>📝</span> Blog Posts
            {stats?.blog?.pending > 0 && (
              <span className={styles.badge}>{stats.blog.pending}</span>
            )}
          </button>
        </nav>

        <div className={styles.sidebarFooter}>
          <button className={styles.logoutButton} onClick={onLogout}>
            🚪 Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={styles.main}>
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className={styles.overview}>
            <h1>📊 Dashboard Overview</h1>
            
            {loading ? (
              <div className={styles.loading}>
                <div className={styles.spinner}></div>
                <p>Loading stats...</p>
              </div>
            ) : stats && (
              <>
                {/* Stats Grid */}
                <div className={styles.statsGrid}>
                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>👥</div>
                    <div className={styles.statInfo}>
                      <span className={styles.statValue}>{stats.users.total}</span>
                      <span className={styles.statLabel}>Total Users</span>
                    </div>
                  </div>
                  
                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>✅</div>
                    <div className={styles.statInfo}>
                      <span className={styles.statValue}>{stats.users.active}</span>
                      <span className={styles.statLabel}>Active Users</span>
                    </div>
                  </div>
                  
                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>🆕</div>
                    <div className={styles.statInfo}>
                      <span className={styles.statValue}>{stats.users.new_this_week}</span>
                      <span className={styles.statLabel}>New This Week</span>
                    </div>
                  </div>
                  
                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>🛡️</div>
                    <div className={styles.statInfo}>
                      <span className={styles.statValue}>{stats.users.staff}</span>
                      <span className={styles.statLabel}>Staff Members</span>
                    </div>
                  </div>
                </div>

                {/* Blog Stats */}
                <h2>📝 Blog Statistics</h2>
                <div className={styles.statsGrid}>
                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>📄</div>
                    <div className={styles.statInfo}>
                      <span className={styles.statValue}>{stats.blog.total_posts}</span>
                      <span className={styles.statLabel}>Total Posts</span>
                    </div>
                  </div>
                  
                  <div className={`${styles.statCard} ${styles.pending}`}>
                    <div className={styles.statIcon}>⏳</div>
                    <div className={styles.statInfo}>
                      <span className={styles.statValue}>{stats.blog.pending}</span>
                      <span className={styles.statLabel}>Pending Review</span>
                    </div>
                  </div>
                  
                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>✅</div>
                    <div className={styles.statInfo}>
                      <span className={styles.statValue}>{stats.blog.approved}</span>
                      <span className={styles.statLabel}>Approved</span>
                    </div>
                  </div>
                  
                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>❌</div>
                    <div className={styles.statInfo}>
                      <span className={styles.statValue}>{stats.blog.rejected}</span>
                      <span className={styles.statLabel}>Rejected</span>
                    </div>
                  </div>
                </div>

                {/* Activity Stats */}
                <h2>📈 Activity Statistics</h2>
                <div className={styles.statsGrid}>
                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>📋</div>
                    <div className={styles.statInfo}>
                      <span className={styles.statValue}>{stats.activity.total_logs}</span>
                      <span className={styles.statLabel}>Total Logs</span>
                    </div>
                  </div>
                  
                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>🌟</div>
                    <div className={styles.statInfo}>
                      <span className={styles.statValue}>{stats.activity.clean_days}</span>
                      <span className={styles.statLabel}>Clean Days Logged</span>
                    </div>
                  </div>
                </div>

                {/* Recent Users */}
                <h2>🆕 Recent Signups</h2>
                <div className={styles.recentList}>
                  {stats.recent_users.map(user => (
                    <div key={user.id} className={styles.recentItem}>
                      <span className={styles.userAvatar}>
                        {user.username.charAt(0).toUpperCase()}
                      </span>
                      <div className={styles.recentInfo}>
                        <span className={styles.recentName}>{user.username}</span>
                        <span className={styles.recentDate}>
                          Joined {formatDate(user.date_joined)}
                        </span>
                      </div>
                      <span className={`${styles.status} ${user.is_active ? styles.active : styles.inactive}`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Quick Actions */}
                <div className={styles.quickActions}>
                  <h2>⚡ Quick Actions</h2>
                  <div className={styles.actionButtons}>
                    <button onClick={() => setActiveTab('users')}>
                      👥 Manage Users
                    </button>
                    <button onClick={() => setActiveTab('blog')}>
                      📝 Review Posts ({stats.blog.pending} pending)
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className={styles.usersTab}>
            <h1>👥 User Management</h1>
            
            {/* Filters */}
            <div className={styles.filters}>
              <div className={styles.filterTabs}>
                {[
                  { value: 'all', label: 'All Users' },
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' },
                  { value: 'staff', label: 'Staff' }
                ].map(f => (
                  <button
                    key={f.value}
                    className={`${styles.filterTab} ${userFilter === f.value ? styles.active : ''}`}
                    onClick={() => { setUserFilter(f.value); setUserPage(1); }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              
              <input
                type="text"
                placeholder="Search users..."
                value={userSearch}
                onChange={(e) => { setUserSearch(e.target.value); setUserPage(1); }}
                className={styles.searchInput}
              />
            </div>

            {/* Users Table */}
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Joined</th>
                    <th>Last Login</th>
                    <th>Streak</th>
                    <th>Status</th>
                    <th>Role</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id}>
                      <td>
                        <div className={styles.userCell}>
                          <span className={styles.userAvatar}>
                            {user.username.charAt(0).toUpperCase()}
                          </span>
                          {user.username}
                        </div>
                      </td>
                      <td>{user.email || '-'}</td>
                      <td>{formatDate(user.date_joined)}</td>
                      <td>{formatDate(user.last_login)}</td>
                      <td>
                        <span className={styles.streak}>
                          🔥 {user.current_streak} / {user.longest_streak}
                        </span>
                      </td>
                      <td>
                        <span className={`${styles.statusBadge} ${user.is_active ? styles.active : styles.inactive}`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        {user.is_superuser ? (
                          <span className={styles.roleBadge}>👑 Super</span>
                        ) : user.is_staff ? (
                          <span className={styles.roleBadge}>🛡️ Staff</span>
                        ) : (
                          <span className={styles.roleUser}>User</span>
                        )}
                      </td>
                      <td>
                        <button
                          className={styles.actionButton}
                          onClick={() => setSelectedUser(user)}
                          disabled={user.id === userData.id}
                        >
                          ⚙️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {userPagination.total_pages > 1 && (
              <div className={styles.pagination}>
                <button
                  disabled={userPage === 1}
                  onClick={() => setUserPage(userPage - 1)}
                >
                  ← Previous
                </button>
                <span>Page {userPage} of {userPagination.total_pages}</span>
                <button
                  disabled={userPage === userPagination.total_pages}
                  onClick={() => setUserPage(userPage + 1)}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        )}

        {/* Blog Tab */}
        {activeTab === 'blog' && (
          <div className={styles.blogTab}>
            <h1>📝 Blog Moderation</h1>
            
            {/* Status Filter */}
            <div className={styles.filters}>
              <div className={styles.filterTabs}>
                {[
                  { value: 'pending', label: '⏳ Pending' },
                  { value: 'approved', label: '✅ Approved' },
                  { value: 'rejected', label: '❌ Rejected' }
                ].map(f => (
                  <button
                    key={f.value}
                    className={`${styles.filterTab} ${postStatusFilter === f.value ? styles.active : ''}`}
                    onClick={() => setPostStatusFilter(f.value)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Posts Grid */}
            <div className={styles.postsGrid}>
              {posts.length === 0 ? (
                <div className={styles.empty}>
                  <span>📭</span>
                  <p>No {postStatusFilter} posts</p>
                </div>
              ) : (
                posts.map(post => (
                  <div 
                    key={post.id} 
                    className={styles.postCard}
                    onClick={() => setSelectedPost(post)}
                  >
                    <div className={styles.postHeader}>
                      {post.category && (
                        <span className={styles.postCategory}>
                          {post.category.icon} {post.category.name}
                        </span>
                      )}
                      <span className={styles.postDate}>{formatDate(post.created_at)}</span>
                    </div>
                    <h3>{post.title}</h3>
                    <p>{post.excerpt}</p>
                    <div className={styles.postAuthor}>
                      <span className={styles.userAvatar}>
                        {post.author.username.charAt(0).toUpperCase()}
                      </span>
                      {post.author.username}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </main>

      {/* User Action Modal */}
      {selectedUser && (
        <div className={styles.modal} onClick={() => setSelectedUser(null)}>
          <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
            <h2>User Actions</h2>
            <div className={styles.modalUserInfo}>
              <span className={styles.bigAvatar}>
                {selectedUser.username.charAt(0).toUpperCase()}
              </span>
              <h3>{selectedUser.username}</h3>
              <p>{selectedUser.email || 'No email'}</p>
            </div>
            
            <div className={styles.modalActions}>
              {selectedUser.is_active ? (
                <button 
                  className={styles.warnButton}
                  onClick={() => handleUserAction(selectedUser.id, 'deactivate')}
                  disabled={actionLoading === selectedUser.id}
                >
                  🚫 Deactivate User
                </button>
              ) : (
                <button 
                  className={styles.successButton}
                  onClick={() => handleUserAction(selectedUser.id, 'activate')}
                  disabled={actionLoading === selectedUser.id}
                >
                  ✅ Activate User
                </button>
              )}
              
              {isSuperuser && !selectedUser.is_superuser && (
                <>
                  {selectedUser.is_staff ? (
                    <button 
                      className={styles.warnButton}
                      onClick={() => handleUserAction(selectedUser.id, 'remove_staff')}
                      disabled={actionLoading === selectedUser.id}
                    >
                      🛡️ Remove Staff Status
                    </button>
                  ) : (
                    <button 
                      className={styles.primaryButton}
                      onClick={() => handleUserAction(selectedUser.id, 'make_staff')}
                      disabled={actionLoading === selectedUser.id}
                    >
                      🛡️ Make Staff
                    </button>
                  )}
                  
                  <button 
                    className={styles.dangerButton}
                    onClick={() => handleUserAction(selectedUser.id, 'delete')}
                    disabled={actionLoading === selectedUser.id}
                  >
                    🗑️ Delete User
                  </button>
                </>
              )}
            </div>
            
            <button 
              className={styles.closeButton}
              onClick={() => setSelectedUser(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Post Preview Modal */}
      {selectedPost && (
        <div className={styles.modal} onClick={() => setSelectedPost(null)}>
          <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
            <h2>Post Review</h2>
            
            <div className={styles.postPreview}>
              {selectedPost.category && (
                <span className={styles.previewCategory}>
                  {selectedPost.category.icon} {selectedPost.category.name}
                </span>
              )}
              <h3>{selectedPost.title}</h3>
              <div className={styles.previewMeta}>
                By <strong>{selectedPost.author.username}</strong> · {formatDate(selectedPost.created_at)}
              </div>
              <div className={styles.previewContent}>
                {selectedPost.content.split('\n\n').map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </div>
            
            {postStatusFilter === 'pending' && (
              <div className={styles.moderationActions}>
                <button 
                  className={styles.approveButton}
                  onClick={() => handleApprovePost(selectedPost.id)}
                  disabled={actionLoading === selectedPost.id}
                >
                  ✅ Approve
                </button>
                
                <div className={styles.rejectSection}>
                  <textarea
                    placeholder="Reason for rejection..."
                    value={rejectionReason}
                    onChange={e => setRejectionReason(e.target.value)}
                  />
                  <button 
                    className={styles.rejectButton}
                    onClick={() => handleRejectPost(selectedPost.id)}
                    disabled={actionLoading === selectedPost.id || !rejectionReason.trim()}
                  >
                    ❌ Reject
                  </button>
                </div>
              </div>
            )}
            
            <button 
              className={styles.closeButton}
              onClick={() => setSelectedPost(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
